import type {PexLogger} from '@pexip/logger';
import {noop} from '@pexip/utils/noop';
import type {TextRingBuffer} from '@pexip/utils/textRingBuffer';

const emptyLogEvents: TextRingBuffer = Object.freeze({
    push: noop,
    size: () => 0,
    byteLength: () => 0,
    isEmpty: () => true,
    droppedLines: () => 0,
    truncatedLines: () => 0,
    clear: noop,
    flush: noop,
    toBlobParts: () => [],
    toString: () => '',
    toArray: () => [],
    [Symbol.iterator]: () => [][Symbol.iterator](),
});

/**
 * Creates an inert logger that cannot be enabled by changing its level.
 *
 * @example
 * const logger = createDisabledLogger();
 * logger.level = 'debug';
 * logger.info({token: 'secret'}, 'Ignored');
 */
export const createDisabledLogger = (): PexLogger => {
    const logger: PexLogger = {
        get level() {
            return 'silent';
        },
        set level(_level: string) {
            // Policy failure keeps logging disabled for the process lifetime.
        },
        logEvents: emptyLogEvents,
        getLogs: () => new Blob([], {type: 'text/plain; charset=utf-8'}),
        getLogFileName: () => 'infinity-connect.log',
        getLogFile: () => '',
        downloadLog: noop,
        addRedactPattern: noop,
        redact: () => '[REDACTED]',
        fatal: noop,
        error: noop,
        warn: noop,
        info: noop,
        debug: noop,
        trace: noop,
        silent: noop,
        child: () => logger,
    };

    return Object.freeze(logger);
};
